import React, { useContext, useState } from 'react';
import { CustomerHistory, ArrayOfContactType, ArrayOfLinkEventType, ArrayOfString, ArrayOfTenant, ContactType, LinkEventType, ReportingFilterCriteria, ReportingFilterCriteriaListItem, Tenant, TimeInterval, TimeRange } from '../../../types/apimodel';
import { Order } from '../../../types/types';
import { getTimeRangeForEnum } from '../../../util/util';

/**
 * Diese Kontext hält die Filter- und Sortiereinstellungen des Reports.
 */

// *** ReportFilter Context Type
type FilterCtxType = {
    filter: ReportingFilterCriteria,
    setFilter: (prevState: ReportingFilterCriteria, doNotFireFilterChanged?: boolean) => void,
    createdFilter: TimeInterval,
    setCreatedFilter: (timeInterval: TimeInterval, doNotFireFilterChanged?: boolean) => void,
    eventFilter: LinkEventType[],
    setEventFilter: (evt: LinkEventType[], doNotFireFilterChanged?: boolean) => void,
    notEventFilter: LinkEventType[],
    setNotEventFilter: (evt: LinkEventType[], doNotFireFilterChanged?: boolean) => void,
    tenantFilter: Tenant[],
    setTenantFilter: (tenant: Tenant[], doNotFireFilterChanged?: boolean) => void,
    contactFilter: string | undefined, 
    setContactFilter: (contact: string | undefined) => void, 
    contactTypeFilter: ContactType[],
    setContactTypeFilter: (contactType: ContactType[]) => void,
    nameFilter: string | undefined, 
    setNameFilter: (name: string | undefined) => void, 
    templateFilter: string[], 
    setTemplateFilter: (templateExtIds: string[]) => void, 
    linkIdFilter: string | undefined, 
    setLinkIdFilter: (linkExtId: string | undefined) => void, 
    filterChanged: boolean,
    resetFilterChanged: () => void,
    reload: boolean,
    fireReload: () => void,
    loadFilterFromConfig: (filterItem: ReportingFilterCriteriaListItem) => void,
    sortOrder: Order,
    setSortOrder: (order:Order, doNotFireFilterChanged?: boolean) => void,
    orderBy: string, 
    setOrderBy: (orderBy: keyof CustomerHistory, doNotFireFilterChanged?: boolean) => void
}

// *** init ReportFilter Context

const filterInitBo:ReportingFilterCriteria = {
    // Alle Mandanten
    tenant:{value:[Tenant[String(Tenant.RHEINLAND) as keyof typeof Tenant], Tenant[String(Tenant.RHION) as keyof typeof Tenant], Tenant[String(Tenant.CREDITLIFE) as keyof typeof Tenant]]},
    createdBetween: getTimeRangeForEnum(TimeInterval[String(TimeInterval.MONTH) as keyof typeof TimeInterval]),
    eventType: {value: []},
    notEventType: {value: []},    
    contactType: {value: []},
};

// *** Create Context
export const FilterCtx = React.createContext<FilterCtxType | undefined>(undefined);

// *** ReportFilter Provider
    type Props = {
        children?: React.ReactNode
    };
    export const FilterProvider: React.FC<Props> = ({ children }) => {
    // *************************
    // State Object / Operation
    // *************************    
    const [filter, setFilter] = useState(filterInitBo);
    const [createdFilter, setCreatedFilter] = useState<TimeInterval>(TimeInterval[String(TimeInterval.MONTH) as keyof typeof TimeInterval]);
    const [eventFilter, setEventFilter] = useState<LinkEventType[]>([]);
    const [tenantFilter, setTenantFilter] = useState<Tenant[]>([Tenant[String(Tenant.RHEINLAND) as keyof typeof Tenant], Tenant[String(Tenant.RHION) as keyof typeof Tenant], Tenant[String(Tenant.CREDITLIFE) as keyof typeof Tenant]]);
    const [notEventFilter, setNotEventFilter] = useState<LinkEventType[]>([]);
    const [contactFilter, setContactFilter] = useState<string | undefined>(undefined);
    const [contactTypeFilter, setContactTypeFilter] = useState<ContactType[]>([]);
    const [nameFilter, setNameFilter] = useState<string | undefined>(undefined);
    const [templateFilter, setTemplateFilter] = useState<string[]>([]);
    const [linkIdFilter, setLinkIdFilter] = useState<string | undefined>(undefined);
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [reload, fireReload] = useState<boolean>(false);
    const [sortOrder, setSortOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof CustomerHistory>('erstelldatum');

    const setFilterClone = (filter: ReportingFilterCriteria) => {
        setFilter({ ...filter });
        setFilterChanged(true);
        fireReload(!reload);
    }

    const setCreatedFilterClone = (timeInterval: TimeInterval) => {
        setCreatedFilter(timeInterval);
        let createdBetween:TimeRange = getTimeRangeForEnum(timeInterval);
        setFilterClone({...filter, createdBetweenAsTimeInterval: timeInterval, createdBetween: createdBetween});
    }

    const fireReloadClone = () => {
        fireReload(!reload);
    }

    const resetFilterChangedClone = () => {
        setFilterChanged(false);
    }


    const setTenantFilterClone = (tenants: Tenant[]) => {
        setTenantFilter(tenants);
        let filterValue: ArrayOfTenant = {value:tenants};
        setFilterClone({...filter, tenant: filterValue});
    }

    const setEventFilterClone = (evt: LinkEventType[]) => {
        setEventFilter(evt);
        let filterValue: ArrayOfLinkEventType = {value:evt};
        setFilterClone({...filter, eventType: filterValue});
    }

    const setNotEventFilterClone = (evt: LinkEventType[]) => {
        setNotEventFilter(evt);
        let filterValue: ArrayOfLinkEventType = {value:evt};
        setFilterClone({...filter, notEventType: filterValue});
    }
    
    const setSortOrderClone = (order: Order) => {
        setSortOrder(order);
        setFilterChanged(true);
        fireReloadClone();
    }

    const setOrderByClone = (orderBy: keyof CustomerHistory) => {
        setOrderBy(orderBy);
        setFilterChanged(true);
        fireReloadClone();
    }

    const setContactFilterClone = (value: string|undefined) => {
        setContactFilter(value);
        setFilterClone({...filter, contact: value});
    }

    const setContactTypeFilterClone = (value: ContactType[]) => {
        setContactTypeFilter(value);
        let filterValue: ArrayOfContactType = {value: value};
        setFilterClone({...filter, contactType: filterValue});
    }

    const setNameFilterClone = (value: string|undefined) => {
        setNameFilter(value);
        setFilterClone({...filter, customername: value});
    }

    const setTemplateFilterClone = (value: string[]) => {
        setTemplateFilter(value);
        let filterValue: ArrayOfString = {value: value};
        setFilterClone({...filter, templateId: filterValue});
    }

    const setLinkIdFilterClone = (value: string|undefined) => {
        setLinkIdFilter(value);
        setFilterClone({...filter, linkid: value});
    }

    /**
     * Setzt die Filter aus der Response vom Server.
     * Alle hier in einer Methode, damit am Ende nur einmal "filter" im State gesetzt wird und wir keine
     * async-Probleme beim Setzen des States durch mehrmalige Aufrufe der verschiedenen Methoden bekommen.
     * 
     * @param filterItem 
     */
    const loadFilterFromConfigClone = (filterItem: ReportingFilterCriteriaListItem) => {

        let newFilter: ReportingFilterCriteria = {...filter};

        if (filterItem.filterCriteria.createdBetweenAsTimeInterval !== undefined) {
            setCreatedFilter(filterItem.filterCriteria.createdBetweenAsTimeInterval);
            let createdBetween:TimeRange = getTimeRangeForEnum(filterItem.filterCriteria.createdBetweenAsTimeInterval);
            newFilter.createdBetween = createdBetween;
        }
        // Name
        setNameFilter(filterItem.filterCriteria.customername);
        newFilter.customername = filterItem.filterCriteria.customername;
        // Link ID
        setLinkIdFilter(filterItem.filterCriteria.linkid);
        newFilter.linkid = filterItem.filterCriteria.linkid;
        // Kontakt
        setContactFilter(filterItem.filterCriteria.contact);
        newFilter.contact = filterItem.filterCriteria.contact;
        // template
        if (filterItem.filterCriteria.templateId !== undefined && filterItem.filterCriteria.templateId != null) {
            setTemplateFilter(filterItem.filterCriteria.templateId.value);
            let filterValue: ArrayOfString = {value: filterItem.filterCriteria.templateId.value};
            newFilter.templateId = filterValue;
        }
        if (filterItem.filterCriteria.eventType !== undefined && filterItem.filterCriteria.eventType !== null) {
            setEventFilter(filterItem.filterCriteria.eventType.value);
            let filterValue: ArrayOfLinkEventType = {value:filterItem.filterCriteria.eventType.value};
            newFilter.eventType = filterValue;    
        }
        if (filterItem.filterCriteria.notEventType !== undefined && filterItem.filterCriteria.notEventType !== null) {
            setNotEventFilter(filterItem.filterCriteria.notEventType.value);
            let filterValue: ArrayOfLinkEventType = {value:filterItem.filterCriteria.notEventType.value};
            newFilter.notEventType = filterValue;    
        }
       
        if (filterItem.filterCriteria.tenant !== undefined && filterItem.filterCriteria.tenant !== null) {
            setTenantFilter(filterItem.filterCriteria.tenant.value);
            let filterValue: ArrayOfTenant = {value:filterItem.filterCriteria.tenant.value};
            newFilter.tenant = filterValue;
        }
        if (filterItem.filterCriteria.contactType !== undefined && filterItem.filterCriteria.contactType !== null) {
            setContactTypeFilter(filterItem.filterCriteria.contactType.value);
            let filterValue: ArrayOfContactType = {value:filterItem.filterCriteria.contactType.value};
            newFilter.contactType = filterValue;
        }
        setFilter(newFilter);
        setOrderBy(String(filterItem.orderBy) as keyof CustomerHistory);
        setSortOrder(filterItem.sortOrder as Order);

        setFilterChanged(true);
        fireReloadClone();
    }

    
    return <FilterCtx.Provider value={{
        filter: filter,
        setFilter: setFilterClone,
        createdFilter: createdFilter,
        setCreatedFilter: setCreatedFilterClone,
        filterChanged: filterChanged,
        resetFilterChanged: resetFilterChangedClone,
        reload: reload,
        fireReload: fireReloadClone,
        eventFilter: eventFilter,
        setEventFilter: setEventFilterClone,
        notEventFilter: notEventFilter,
        setNotEventFilter: setNotEventFilterClone,
        tenantFilter: tenantFilter,
        setTenantFilter: setTenantFilterClone,
        sortOrder: sortOrder,
        setSortOrder: setSortOrderClone,
        orderBy: orderBy,
        setOrderBy: setOrderByClone,
        loadFilterFromConfig: loadFilterFromConfigClone,
        contactFilter: contactFilter, 
        setContactFilter: setContactFilterClone,
        contactTypeFilter: contactTypeFilter, 
        setContactTypeFilter: setContactTypeFilterClone,
        nameFilter: nameFilter, 
        setNameFilter: setNameFilterClone,
        templateFilter: templateFilter, 
        setTemplateFilter: setTemplateFilterClone,
        linkIdFilter: linkIdFilter,
        setLinkIdFilter: setLinkIdFilterClone
    }}>
        {children}
    </FilterCtx.Provider >
}

// *******************
// React Context
// *******************
const useFilterCtx = () => {
    const filterContext = useContext(FilterCtx);
    if (!filterContext)
        throw new Error(
            'Kein FilterCtx.Provider gefunden beim Aufruf von useFilterCtx.'
        );
    return filterContext;

}

export default useFilterCtx;